import { getItemSummaryCatalogId, getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardComponent, ItemCardInfo } from '../../../../types/types';
import {
    ItemCardSource,
    useTrackItemCardClicked,
} from '@/utils/amplitude/analytics/item/trackItemCardClicked.analytics';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { useAppSelector } from '@/redux/hooks';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import ImageRow from '@/components/ItemCard/ImageRow';
import ItemCardPriceRow from '../../../components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '../../../components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '../../../components/ItemCardTitle/ItemCardTitle';
import React from 'react';
import styled from 'styled-components';

const UpcomingItemPageItemCard: ItemCardComponent = ({
    className,
    dimension,
    itemId,
    recommendationPosition,
    recommendationType,
}) => {
    const [hover, setHover] = React.useState(false);
    const onRecommendationClick = useRecommendationTracking({
        itemId,
        recommendationPosition: recommendationPosition ?? 0,
        recommendationType,
    });
    const trackItemCardClicked = useTrackItemCardClicked();
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));

    const handleMouseEnter = React.useCallback(() => setHover(true), []);
    const handleMouseLeave = React.useCallback(() => setHover(false), []);

    const handleClick = React.useCallback(
        (trigger: LotRecommendationTrigger) => {
            onRecommendationClick(trigger);
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.LotDetailsPage,
            });
        },
        [catalogId, itemId, onRecommendationClick, promoted, trackItemCardClicked]
    );

    return (
        <UpcomingItemPageItemCardContainer
            $dimension={dimension}
            className={className}
            data-testid="upcoming-items-page-item-card"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <StyledImageContainer>
                <StyledFavoriteButton>
                    <ItemCardSaveIcon itemId={itemId} />
                </StyledFavoriteButton>
                <PlacedItemCardImage
                    dimension={dimension}
                    itemId={itemId}
                    onNavigate={() => handleClick(LotRecommendationTrigger.ImageClick)}
                />
            </StyledImageContainer>
            <PlacedItemCardShortDate
                itemId={itemId}
                onMouseEnter={handleMouseLeave}
                onMouseLeave={handleMouseEnter}
            />
            <PlacedItemCardTitle
                data-testid="itemCardTite"
                isHovered={hover}
                itemId={itemId}
                onNavigate={() => handleClick(LotRecommendationTrigger.TitleClick)}
            />
            <PlacedItemCardPriceRow
                isHovered={hover}
                itemId={itemId}
            />
        </UpcomingItemPageItemCardContainer>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 184, mobile: 157 },
    ItemCardComponent: UpcomingItemPageItemCard,
};

export default info;

const StyledImageContainer = styled.section`
    position: relative;
`;

const PlacedItemCardImage = styled(ImageRow)`
    grid-row: img;
`;

const PlacedItemCardShortDate = styled(ItemCardShortDate)`
    grid-row: date;
`;

const PlacedItemCardTitle = styled(ItemCardTitle)`
    grid-row: title;
`;

const PlacedItemCardPriceRow = styled(ItemCardPriceRow)`
    grid-row: soldprice;
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
`;

const UpcomingItemPageItemCardContainer = styled.div<{ $dimension: number }>`
    position: relative;
    display: grid;
    grid-template-columns: ${({ $dimension }) => ($dimension ? `${$dimension}px` : '1fr')};
    grid-template-rows: min-content 8px 20px 4px min-content 4px min-content;
    grid-template-areas:
        'img'
        '.'
        'date'
        '.'
        'title'
        '.'
        'soldprice';
`;
